/*eslint no-loop-func: "off"*/
import {
  ApolloClient,
  InMemoryCache,
  createHttpLink,
  from,
  fromPromise,
} from '@apollo/client'
import { setContext } from '@apollo/client/link/context'
import { onError } from '@apollo/client/link/error'

import { clearAuthStorage, handleAuthResult } from './auth'
import {
  REFRESH_TOKEN,
  RefreshTokenData,
  RefreshTokenVars,
} from './graphql/mutation'

const getAccessToken = () => localStorage.getItem('accessToken')
const getRefreshToken = () => localStorage.getItem('refreshToken') || ''

let isRefreshing = false
let isRedirectingToSso = false
let pendingRequests: Array<{
  resolve: () => void
  reject: (error?: unknown) => void
}> = []

const resolvePendingRequests = () => {
  pendingRequests.forEach(({ resolve }) => resolve())
  pendingRequests = []
}

const rejectPendingRequests = (error: Error) => {
  pendingRequests.forEach(({ reject }) => reject(error))
  pendingRequests = []
}

const errorLink = onError(
  ({ graphQLErrors, networkError, operation, forward }) => {
    // Let refresh failures reach the cleanup below instead of waiting on themselves.
    if (operation.getContext().skipAuthRefresh) return

    if (graphQLErrors) {
      for (let err of graphQLErrors) {
        switch (err.extensions!.code) {
          case 'UNAUTHENTICATED': {
            if (isRedirectingToSso) {
              return
            }

            const refreshToken = getRefreshToken()

            if (refreshToken === '') {
              clearAuthStorage()
              rejectPendingRequests(new Error('Missing refresh token'))
              return
            }

            let forward$

            if (!isRefreshing) {
              isRefreshing = true
              forward$ = fromPromise(
                client
                  .mutate<RefreshTokenData, RefreshTokenVars>({
                    mutation: REFRESH_TOKEN,
                    context: { skipAuthRefresh: true, skipAuthorization: true },
                    variables: {
                      refreshToken,
                    },
                  })
                  .then(({ data }) => {
                    const outcome = handleAuthResult(data?.refresh || {})

                    if (outcome === 'redirected') {
                      isRedirectingToSso = true
                      const error = new Error('Redirecting to SSO')
                      rejectPendingRequests(error)
                      throw error
                    }

                    if (outcome === 'invalid') {
                      clearAuthStorage()
                      const error = new Error('Unable to refresh session')
                      rejectPendingRequests(error)
                      throw error
                    }

                    resolvePendingRequests()
                  })
                  .catch((error) => {
                    clearAuthStorage()
                    rejectPendingRequests(
                      error instanceof Error
                        ? error
                        : new Error('Unable to refresh session'),
                    )
                    return Promise.reject(error)
                  })
                  .finally(() => {
                    isRefreshing = false
                  }),
              )
            } else {
              forward$ = fromPromise(
                new Promise<void>((resolve, reject) => {
                  pendingRequests.push({ resolve, reject })
                }),
              )
            }

            return forward$.flatMap(() => forward(operation))
          }
          default:
            console.error(
              `[GraphQL error]: Message: ${err.message}, Location: ${err.locations}, Path: ${err.path}`,
            )
        }
      }
    }

    if (networkError) console.error(`[Network error]: ${networkError}`)
  },
)

const authLink = setContext((_, context) => {
  const accessToken = getAccessToken()

  if (accessToken !== null && !context.skipAuthorization) {
    return {
      ...context,
      headers: {
        ...context.headers,
        authorization: `Bearer ${accessToken}`,
      },
    }
  }

  return context
})

const httpLink = createHttpLink({
  uri: process.env.REACT_APP_GRAPHQL_URL || '/graphql',
})

const client = new ApolloClient({
  link: from([errorLink, authLink, httpLink]),
  cache: new InMemoryCache({
    typePolicies: {
      Query: {
        fields: {
          agentCalls: {
            merge(_, incoming) {
              return incoming
            }
          }
        }
      }
    }
  }),
})

export default client
